import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { X, ExternalLink, Loader2 } from "lucide-react";
import {
  CSE_ID, buildDireccionQuery, buildGoogleFallbackUrl,
} from "@/constants/portales";

const CSE_ELEMENT_NAME = "publicadoResults";
const LOAD_TIMEOUT_MS = 4000;

/**
 * Modal "¿Está publicado?" — Nivel 1 widget de Google (gcse-searchresults-only,
 * sin cajita, disparado por execute()), Nivel 2 link a google.com como
 * paracaídas.
 *
 * REGLAS del sprint UX + Publicado (Gonzalo, 2026-02):
 *  - El widget es best-effort. Si falla → fallback automático (timeout 4s).
 *  - Fallback SIEMPRE visible como opción secundaria — no depende del widget.
 *  - Fallback ancla PURA (target="_blank" rel="noopener noreferrer"). Nada de
 *    window.open ni onClick con location.href. Lección aprendida.
 *  - NO mostrar badge PUBLICADO/NO PUBLICADO: el humano lee y decide.
 *
 * Trap resuelta (2026-02-15, diagnóstico con timeline segundo-a-segundo):
 *  Google inyecta ~4KB de resultados en el slot a t≈1s. Pero React re-renderiza
 *  (por state changes internos del dialog) y `dangerouslySetInnerHTML` reconcilia
 *  el subtree — borra los nodos de Google. Solución: ref manual + injection
 *  imperativa una única vez + prop `__html` con referencia estable (React NO
 *  reconcilia si el objeto no cambia). Google conserva sus nodos.
 */
export default function PublicadoDialog({ lote, onClose }) {
  const [scriptState, setScriptState] = useState("loading"); // loading | ready | error
  const info = buildDireccionQuery(lote);
  const slotRef = useRef(null);
  const injected = useRef(false);
  // Referencia estable — React nunca reconcilia el subtree porque el objeto
  // __html es siempre el mismo.
  const stableEmpty = useMemo(() => ({ __html: "" }), []);

  useLayoutEffect(() => {
    if (!injected.current && slotRef.current) {
      slotRef.current.innerHTML = `<div class="gcse-searchresults-only" data-gname="${CSE_ELEMENT_NAME}" data-linkTarget="_blank"></div>`;
      injected.current = true;
    }
  }, []);

  useEffect(() => {
    if (!info || !CSE_ID) {
      setScriptState("error");
      return;
    }
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (!cancelled && scriptState === "loading") {
        setScriptState("error");
      }
    }, LOAD_TIMEOUT_MS);

    // Reutiliza script si ya está en el head
    const existing = document.querySelector(`script[data-cse-cx="${CSE_ID}"]`);
    const executeQuery = () => {
      try {
        const element = window.google?.search?.cse?.element?.getElement(CSE_ELEMENT_NAME);
        if (element) {
          element.execute(info.query);
          if (!cancelled) setScriptState("ready");
        } else {
          // Element aún no registrado — reintenta con parse (procesa las divs)
          window.google?.search?.cse?.element?.render({
            div: CSE_ELEMENT_NAME + "_target",
            tag: "searchresults-only",
            gname: CSE_ELEMENT_NAME,
            attributes: { linkTarget: "_blank" },
          });
          const el2 = window.google?.search?.cse?.element?.getElement(CSE_ELEMENT_NAME);
          if (el2) {
            el2.execute(info.query);
            if (!cancelled) setScriptState("ready");
          }
        }
      } catch (e) {
        if (!cancelled) setScriptState("error");
      }
    };

    if (existing && window.google?.search?.cse) {
      executeQuery();
    } else if (existing) {
      // Script está pero no terminó de bootear
      existing.addEventListener("load", executeQuery, { once: true });
      existing.addEventListener("error", () => !cancelled && setScriptState("error"), { once: true });
    } else {
      const script = document.createElement("script");
      script.src = `https://cse.google.com/cse.js?cx=${CSE_ID}`;
      script.async = true;
      script.setAttribute("data-cse-cx", CSE_ID);
      script.onload = executeQuery;
      script.onerror = () => !cancelled && setScriptState("error");
      document.head.appendChild(script);
    }

    return () => { cancelled = true; clearTimeout(timeoutId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info?.query]);

  // Cierre con ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!info) return null;

  const fallbackUrl = buildGoogleFallbackUrl(info.queryPlana);

  return (
    <div className="fixed inset-0 z-[1000] flex items-start justify-center pt-16 p-4" data-testid="publicado-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative w-full max-w-3xl bg-white shadow-2xl max-h-[85vh] flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="px-6 py-4 border-b-2 border-slate-900 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-lg text-slate-900">🔎 ¿Está publicado?</h2>
            <p className="text-sm text-slate-700 mt-0.5 truncate" data-testid="publicado-direccion">
              {lote?.direccion || "sin dirección"}
              {lote?.barrio && <span className="text-slate-500"> · {lote.barrio}</span>}
            </p>
            <p
              className={`text-xs mt-1 font-medium ${info.tipo === "exacta" ? "text-emerald-700" : "text-amber-700"}`}
              data-testid="publicado-tipo-etiqueta"
            >
              {info.etiqueta}
            </p>
          </div>
          <button
            data-testid="publicado-close"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Texto permanente (honestidad) */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
          <p className="text-xs text-slate-600 leading-relaxed" data-testid="publicado-disclaimer">
            Busca esta dirección en los 7 portales inmobiliarios. Si no aparece, puede
            que no esté publicado — o que el aviso escriba la dirección distinto.
          </p>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {scriptState === "loading" && (
            <div className="flex items-center gap-2 text-sm text-slate-600 mb-4" data-testid="publicado-loading">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando resultados de Google…
            </div>
          )}

          {/* Nota: si el widget embebido falla (scriptState === "error"),
              NO mostramos advertencia técnica al usuario. El botón "Ver en
              Google" del footer ya es la vía canónica de búsqueda. */}

          {/* Widget: gcse-searchresults-only — sin cajita, ejecutado por API.
              Div inyectado imperativamente vía ref (una única vez) + prop
              __html estable → React nunca reconcilia el subtree. Google
              conserva sus nodos. Ver comentario del componente. */}
          <div
            ref={slotRef}
            data-testid="publicado-widget-slot"
            dangerouslySetInnerHTML={stableEmpty}
          />
        </div>

        {/* Footer con fallback (SIEMPRE visible) */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-slate-500">
            Fuentes: Zonaprop · Argenprop · MercadoLibre · Properati · Remax · La Voz · Agrofy
          </p>
          <a
            href={fallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            referrerPolicy="no-referrer"
            data-testid="publicado-fallback-link"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-900 hover:text-emerald-700 border border-slate-400 px-3 py-1.5 hover:border-emerald-700 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Ver en Google
          </a>
        </div>
      </div>
    </div>
  );
}
